import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import type { ContactNote, ContactNoteListResponse } from '@/lib/types'

export function useContactNotes(contactId: string | undefined, page = 1, search?: string) {
  const params = new URLSearchParams({ page: String(page) })
  if (search) params.set('q', search)

  return useQuery({
    queryKey: ['contact-notes', contactId, page, search],
    queryFn: () => api<ContactNoteListResponse>(`/api/contacts/${contactId}/notes?${params}`),
    enabled: !!contactId,
  })
}

export function useCreateContactNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      contactId,
      content,
      occurred_at,
    }: {
      contactId: string
      content: string
      occurred_at?: string
    }) =>
      api<ContactNote>(`/api/contacts/${contactId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ content, occurred_at }),
      }),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['contact-notes', variables.contactId] })
      toast.success(i18n.t('toast.noteCreated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useUpdateContactNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api<ContactNote>(`/api/notes/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-notes'] })
      toast.success(i18n.t('toast.noteUpdated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useDeleteContactNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/notes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-notes'] })
      toast.success(i18n.t('toast.noteDeleted'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}
